import type { CustomNode } from '@/types/node-types';

/**
 * Keeps "a default the editor filled in for display" out of what we save.
 *
 * When a workflow is opened, `reconcileParamsWithCatalog` copies a param's
 * catalog default into an empty saved value so dropdowns and text fields show
 * something sensible. Until 1 Nov 2026 that copy was also persisted on the
 * next save, so opening a workflow and pressing Run rewrote every untouched
 * `""` as its default (`error_code: "validation.failed"`, `config_version:
 * "1.0"`, `values: []`, ...). Nothing changed for the run itself (the backend
 * fills the same default for `""`), but the loop-region compiler compared the
 * authored value with the catalog's `value` and rejected a Validate / Assert
 * judge whose `error_code` suddenly read `validation.failed`
 * (algo-merge-sort, run 9f01254c).
 *
 * The rule now: a value the editor filled and the user never touched goes
 * back to what was loaded when the workflow is saved. A value the user set
 * (even to the same text as the default, after changing it) is theirs and is
 * kept as long as it differs from the loaded value's filled form; a value they
 * set to something else is always kept.
 */

interface FilledDefault {
  /** What the saved workflow had before the editor filled it (`""`/`null`). */
  original: unknown;
  /** The catalog default the editor put in its place for display. */
  applied: unknown;
}

const filledByNode = new Map<string, Map<string, FilledDefault>>();

const canonical = (value: unknown): string => {
  const sort = (input: unknown): unknown => {
    if (Array.isArray(input)) return input.map(sort);
    if (input && typeof input === 'object') {
      return Object.keys(input as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = sort((input as Record<string, unknown>)[key]);
          return acc;
        }, {});
    }
    return input;
  };
  return JSON.stringify(sort(value));
};

/** Structural equality that ignores object key order. */
export const sameValue = (a: unknown, b: unknown): boolean =>
  canonical(a) === canonical(b);

/** Forget every record; call when a workflow is (re)loaded. */
export const resetUntouchedDefaults = (): void => {
  filledByNode.clear();
};

/** Remember that `applied` was shown in place of `original` for this field. */
export const recordUntouchedDefault = (
  nodeId: string,
  fieldId: string,
  original: unknown,
  applied: unknown,
): void => {
  let fields = filledByNode.get(nodeId);
  if (!fields) {
    fields = new Map();
    filledByNode.set(nodeId, fields);
  }
  fields.set(fieldId, { original, applied });
};

/** Number of fields currently remembered (tests and diagnostics). */
export const untouchedDefaultCount = (): number => {
  let count = 0;
  filledByNode.forEach((fields) => {
    count += fields.size;
  });
  return count;
};

/**
 * Return the nodes as they should be persisted: every field whose value is
 * still exactly the default the editor filled goes back to the loaded value.
 * Nodes and fields the editor never touched are returned as-is (same object).
 */
export const restoreUntouchedDefaults = (nodes: CustomNode[]): CustomNode[] => {
  if (filledByNode.size === 0) return nodes;
  return nodes.map((node) => {
    const fields = filledByNode.get(node.id);
    const params = node.data?.struct?.params;
    if (!fields || !Array.isArray(params)) return node;
    let changed = false;
    const restored = params.map((param) => {
      const entry = fields.get(param.field_id);
      if (!entry || !sameValue(param.value, entry.applied)) return param;
      changed = true;
      return { ...param, value: entry.original as typeof param.value };
    });
    if (!changed) return node;
    return {
      ...node,
      data: {
        ...node.data,
        struct: { ...node.data.struct, params: restored },
      },
    };
  });
};
